import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

const MAX_CSV_BYTES = 5 * 1024 * 1024;

const csvPayloadSchema = z.object({
    filename: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,79}\.csv$/),
    content_type: z.literal('text/csv; charset=utf-8'),
    content: z.string(),
    columns: z.array(z.string().min(1)).min(1),
    row_count: z.number().int().nonnegative(),
    column_count: z.number().int().positive(),
    size_bytes: z.number().int().nonnegative().max(MAX_CSV_BYTES),
});

export function AdminCsvArtifact({ artifact }: AdminArtifactRendererProps) {
    const parsed = csvPayloadSchema.safeParse(toRecord(artifact.payload));
    if (!parsed.success) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <ArtifactLifecycleStatusBadge
                        lifecycleStatus={artifact.lifecycle_status}
                    />
                }
            >
                <ArtifactEvidence artifact={artifact} />
                <p className="text-muted-foreground text-xs">
                    This CSV artifact does not match the admin.csv@1 contract,
                    so the admin UI cannot prepare a safe download.
                </p>
            </ArtifactFrame>
        );
    }

    const payload = parsed.data;
    return (
        <ArtifactFrame
            artifact={artifact}
            title={payload.filename}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div className="border-border/40 grid grid-cols-3 gap-x-4 border-t pt-2.5 text-xs">
                <CsvMetadata label="Rows" value={String(payload.row_count)} />
                <CsvMetadata
                    label="Columns"
                    value={String(payload.column_count)}
                />
                <CsvMetadata
                    label="Size"
                    value={formatCsvSize(payload.size_bytes)}
                />
            </div>
            <CsvDownload
                content={payload.content}
                contentType={payload.content_type}
                filename={payload.filename}
            />
        </ArtifactFrame>
    );
}

function CsvDownload({
    content,
    contentType,
    filename,
}: {
    readonly content: string;
    readonly contentType: string;
    readonly filename: string;
}) {
    const downloadUrl = useCsvDownloadUrl(content, contentType);
    if (!downloadUrl) {
        return null;
    }
    return (
        <a
            className="bg-foreground text-background inline-flex w-fit rounded-md px-2.5 py-1.5 text-xs font-medium transition-opacity hover:opacity-85"
            data-testid="admin-csv-artifact-download"
            download={filename}
            href={downloadUrl}
        >
            Download CSV
        </a>
    );
}

function CsvMetadata({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div>
            <div className="text-muted-foreground/70 text-[11px]">{label}</div>
            <div className="text-foreground mt-0.5 font-medium tabular-nums">
                {value}
            </div>
        </div>
    );
}

function useCsvDownloadUrl(
    content: string,
    contentType: string,
): string | null {
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        const url = URL.createObjectURL(
            new Blob([content], { type: contentType }),
        );
        setDownloadUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [content, contentType]);

    return downloadUrl;
}

function formatCsvSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
}
